import { useEffect, useRef, useState, useCallback, useContext } from "react";
import { ITimeEntry, IUser } from "../types/types";
import { AppContext } from "../providers/ContextProvider";

interface TimeEntryChangeEvent {
    type: "created" | "updated" | "deleted";
    timeEntry: ITimeEntry | null;
    userId: string;
}

interface UserSettingsChangeEvent {
    type: "updated" | "created" | "deleted";
    user: IUser | null;
    userId: string;
}

type WebSocketEvent = TimeEntryChangeEvent | UserSettingsChangeEvent;

interface WebSocketMessage {
    type: "connected" | "pong" | "timeEntryChange" | "userSettingsChange";
    event?: WebSocketEvent;
    message?: string;
    // Backend also sends the updated user directly on userSettingsChange for convenience
    user?: IUser;
}

interface UseRealtimeTimeEntriesOptions {
    token: string | undefined;
    onTimeEntryChange?: (event: TimeEntryChangeEvent) => void;
    onUserSettingsChange?: (user: IUser) => void;
    enabled?: boolean;
}

interface UseRealtimeTimeEntriesReturn {
    isConnected: boolean;
    reconnect: () => void;
}

const PING_INTERVAL = 30000; // 30 seconds
const RECONNECT_DELAY = 3000; // 3 seconds
const MAX_RECONNECT_DELAY = 30000; // 30 seconds

/**
 * Custom hook for managing WebSocket connection to receive real-time time entry updates
 *
 * @param options Configuration options
 * @param options.token JWT token for authentication
 * @param options.onTimeEntryChange Callback function called when a time entry change is received
 * @param options.enabled Whether the WebSocket connection should be enabled (default: true)
 * @returns Object with connection status and reconnect function
 */
export function useRealtimeTimeEntries({
    token,
    onTimeEntryChange,
    onUserSettingsChange,
    enabled = true,
}: UseRealtimeTimeEntriesOptions): UseRealtimeTimeEntriesReturn {
    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<number | null>(null);
    const pingIntervalRef = useRef<number | null>(null);
    const reconnectAttemptsRef = useRef<number>(0);
    const onTimeEntryChangeRef = useRef(onTimeEntryChange);
    const onUserSettingsChangeRef = useRef(onUserSettingsChange);
    const [isConnected, setIsConnected] = useState(false);
    const baseUrl = process.env.REACT_APP_APIURL || "";
    const { renewToken } = useContext(AppContext);
    const currentTokenRef = useRef<string | undefined>(token);

    // Keep latest token in a ref so we can use renewed tokens for reconnects
    useEffect(() => {
        currentTokenRef.current = token;
    }, [token]);

    // Keep callback refs up to date without triggering reconnections
    useEffect(() => {
        onTimeEntryChangeRef.current = onTimeEntryChange;
    }, [onTimeEntryChange]);

    useEffect(() => {
        onUserSettingsChangeRef.current = onUserSettingsChange;
    }, [onUserSettingsChange]);

    const cleanup = useCallback(() => {
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
    }, []);

    const connect = useCallback(() => {
        const authToken = currentTokenRef.current || token;

        if (!authToken || !enabled) {
            return;
        }

        try {
            // Determine WebSocket URL (ws:// for local, wss:// for production)
            const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
            const wsUrl = `${protocol}//${baseUrl.replace(/^https?:\/\//, "")}/api/v1/ws?token=${encodeURIComponent(authToken)}`;

            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                console.log("WebSocket connected");
                setIsConnected(true);
                reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection

                // Clear any pending reconnection
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }

                // Send ping every 30 seconds to keep connection alive
                pingIntervalRef.current = window.setInterval(() => {
                    if (ws.readyState === WebSocket.OPEN) {
                        ws.send(JSON.stringify({ type: "ping" }));
                    }
                }, PING_INTERVAL);
            };

            ws.onmessage = (event) => {
                try {
                    const message: WebSocketMessage = JSON.parse(event.data);

                    switch (message.type) {
                        case "connected":
                            console.log("WebSocket:", message.message);
                            break;
                        case "pong":
                            // Keep-alive response
                            break;
                        case "timeEntryChange":
                            if (message.event && onTimeEntryChangeRef.current) {
                                onTimeEntryChangeRef.current(message.event as TimeEntryChangeEvent);
                            }
                            break;
                        case "userSettingsChange": {
                            const updatedUser =
                                message.user ||
                                (message.event && (message.event as UserSettingsChangeEvent).user) ||
                                null;
                            if (updatedUser && onUserSettingsChangeRef.current) {
                                onUserSettingsChangeRef.current(updatedUser);
                            }
                            break;
                        }
                        default:
                            console.warn("Unknown WebSocket message type:", message.type);
                    }
                } catch (error) {
                    console.error("Error parsing WebSocket message:", error);
                }
            };

            ws.onerror = (error) => {
                console.error("WebSocket error:", error);
                setIsConnected(false);
            };

            ws.onclose = (event) => {
                console.log("WebSocket disconnected", event.code, event.reason);
                setIsConnected(false);

                // Clear ping interval
                if (pingIntervalRef.current) {
                    clearInterval(pingIntervalRef.current);
                    pingIntervalRef.current = null;
                }

                // 1000 = normal close (manual / intentional)
                // 1005 = no status code present (often environment / transport-level)
                // To avoid noisy reconnect loops (e.g. Teams browser when resizing to mobile),
                // we do NOT auto-reconnect on 1000 or 1005. Other codes still trigger backoff.
                if (event.code === 1000 || event.code === 1005 || !enabled) {
                    return;
                }

                // Exponential backoff for reconnection
                const delay = Math.min(RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current), MAX_RECONNECT_DELAY);
                reconnectAttemptsRef.current += 1;

                console.log(`Attempting to reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);

                // Before reconnecting, try to renew the token so we don't keep
                // reconnecting with an expired JWT (common cause for 1006 closures)
                const prepareAndReconnect = async () => {
                    if (renewToken) {
                        try {
                            const newToken = await renewToken();
                            currentTokenRef.current = newToken;
                        } catch (renewError) {
                            console.error("Failed to renew token before WebSocket reconnect:", renewError);
                        }
                    }

                    reconnectTimeoutRef.current = window.setTimeout(() => {
                        connect();
                    }, delay);
                };

                void prepareAndReconnect();
            };
        } catch (error) {
            console.error("Error connecting WebSocket:", error);
            setIsConnected(false);
        }
    }, [token, enabled, baseUrl, renewToken]);

    const reconnect = useCallback(() => {
        cleanup();
        reconnectAttemptsRef.current = 0;

        const renewAndConnect = async () => {
            if (renewToken) {
                try {
                    const newToken = await renewToken();
                    currentTokenRef.current = newToken;
                } catch (renewError) {
                    console.error("Failed to renew token before manual WebSocket reconnect:", renewError);
                }
            }

            connect();
        };

        void renewAndConnect();
    }, [cleanup, connect, renewToken]);

    useEffect(() => {
        if (enabled && token) {
            connect();
        } else {
            cleanup();
            setIsConnected(false);
        }

        // Cleanup on unmount or when dependencies change
        return () => {
            cleanup();
        };
    }, [enabled, token, connect, cleanup]);

    return { isConnected, reconnect };
}
